import React, {useContext, useMemo} from "react";
import "./Modal.css";
import {ModalContext} from "../views/contextProviders/ModalProvider";

type ModalButton = {
  /** The name to display on the modal button */
  name: string;
  /** The onClick function for the modal button */
  onClick: () => void;
};

type ModalProps = {
  /** The headline of the modal */
  headline: string;
  /** The text to display on the body of the modal or a node itself */
  body: string | React.ReactNode;
  /** Name and function of the primary button */
  primaryButton: ModalButton;
  /** Name and function of the secondary button */
  secondaryButton?: ModalButton;
};

/**
 * A modal that displays a text and a primary and secondary button
 */
function Modal({headline, body, primaryButton, secondaryButton}: ModalProps) {
  const modalContext = useContext(ModalContext);

  const modalContent = useMemo(
    () => (
      <div className="modal__backdrop">
        <div
          className="modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="modalHeadline"
          aria-describedby="modalText"
          tabIndex={-1}
        >
          <div id="modalHeadline" className="modal__headline">
            {headline}
          </div>
          {typeof body === "string" ? (
            <div id="modalText" className="modal__text">
              {body}
            </div>
          ) : (
            body
          )}
          <div className="modal__buttons">
            {secondaryButton && (
              <button
                id="secondaryModalButton"
                className="textButton"
                onClick={() => {
                  secondaryButton?.onClick();
                  modalContext.hideModal();
                }}
              >
                {secondaryButton.name}
              </button>
            )}
            <button
              id="primaryModalButton"
              className="linkButton"
              onClick={() => {
                primaryButton.onClick();
                modalContext.hideModal();
              }}
            >
              {primaryButton.name}
            </button>
          </div>
        </div>
      </div>
    ),
    [headline, body, primaryButton, secondaryButton, modalContext],
  );

  return modalContent;
}

export {Modal, type ModalProps};
